import { ComponentProps, FC, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Settings } from '@mint-lib/icons';
import { Chip, Input, TruncateText } from '@mint-lib/tw-components';
import {
  CalendarSingle,
  Counter,
  DatepickerInput,
} from '@mint-lib/tw-components/patterns';
import { formatISO, isValid, parseISO } from 'date-fns';

import { ChannelTypeCategory } from '@myn/shared/src/constants';

import type { CellFormatType, ValidatedCell } from '../../api/types.js';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import {
  useActivityChannelNamesList,
  useActivityMediaRowsList,
  useAllPlatformsList,
  useChannelNamesList,
  useChannelsCatalog,
  useDataFilePreview,
  useMediaRowslist,
  usePlatformsOfActivity,
} from '../../queries/index.js';
import { Combobox } from '../atoms/Combobox.jsx';
import { Icon } from '../atoms/Icon.jsx';
import { RichTooltip } from '../atoms/RichTooltip.jsx';

type DynamicFieldProps = Readonly<{
  formatType: CellFormatType;
  cell: ValidatedCell;
  platformCell: ValidatedCell;
  channelCell?: ValidatedCell;
  mediaRowCell?: ValidatedCell;
  onChange: (value: string, displayValue?: string) => void;
}>;

type FieldValue = string | number | Date | null;

export const DynamicField = ({
  cell,
  formatType,
  platformCell,
  channelCell,
  mediaRowCell,
  onChange,
}: DynamicFieldProps) => {
  const fieldVariant = useFieldVariant(formatType) as FieldVariant<FieldValue>;
  const FieldComponent = fieldVariant.component;
  const fieldTransforms = fieldVariant.transforms;

  const initialClientValue = fieldTransforms.toClient(cell.rawCell);
  const [value, setValue] = useState(initialClientValue);

  const handleChange = (v: FieldValue, displayValue?: string) => {
    setValue(v);
    const encodedValue = fieldTransforms.toServer(v);
    onChange(encodedValue, displayValue);
  };

  return (
    <FieldComponent
      value={value}
      onChange={handleChange}
      cell={cell}
      platformCell={platformCell}
      channelCell={channelCell}
      mediaRowCell={mediaRowCell}
    />
  );
};

type FieldProps<Value> = {
  value: Value;
  onChange: (value: Value, label?: string) => void;
  cell: DynamicFieldProps['cell'];
  platformCell: DynamicFieldProps['platformCell'];
  channelCell: DynamicFieldProps['channelCell'];
  mediaRowCell: DynamicFieldProps['mediaRowCell'];
};

const IdTextField = ({
  value,
  onChange,
}: Pick<FieldProps<string>, 'value' | 'onChange'>) => {
  const { t } = useTranslation('@myn/import-hub');

  return (
    <RichTooltip
      asChild
      description={t(
        'Only Latin characters, numbers and special symbols are allowed',
      )}
    >
      <div className="w-full">
        <Input
          data-dd-action-name="Validated cell: edit ID"
          autoFocus
          value={value}
          autoComplete="off"
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      </div>
    </RichTooltip>
  );
};

const FloatField = ({
  value,
  onChange,
}: Pick<FieldProps<null | string>, 'value' | 'onChange'>) => {
  return (
    <Input
      data-dd-action-name="Validated cell: edit number"
      autoFocus
      type="number"
      inputMode="decimal"
      step="any"
      value={value ?? ''}
      onChange={(event) => onChange(event.currentTarget.value || null)}
    />
  );
};

const CounterField = ({
  value,
  onChange,
}: Pick<FieldProps<number>, 'value' | 'onChange'>) => {
  const counterValue = Number.isFinite(value) ? value : undefined;

  return (
    <Counter
      min={0}
      max={Number.MAX_SAFE_INTEGER}
      value={counterValue}
      onChange={onChange}
      inputProps={{ autoFocus: true }}
    />
  );
};

const DateField = ({
  value,
  onChange,
}: Pick<FieldProps<null | Date>, 'value' | 'onChange'>) => {
  const [internalDate, setInternalDate] = useState<Date | undefined>(
    isValid(value) ? (value as Date) : undefined,
  );

  const handleSelect = (date?: Date) => {
    setInternalDate(date);
  };

  const handleApply = (date?: Date) => {
    onChange(date ?? null);
  };

  return (
    <DatepickerInput open={true} fullWidth clearable={false}>
      <CalendarSingle
        selected={internalDate}
        onSelect={handleSelect}
        onApply={handleApply}
      />
    </DatepickerInput>
  );
};

const PlatformField = ({
  value,
  onChange,
  cell,
}: Pick<FieldProps<string>, 'value' | 'onChange' | 'cell'>) => {
  const { t } = useTranslation('@myn/import-hub');
  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V1'>(dataFileId);
  const { data: platforms = [], isLoading } = useAllPlatformsList({
    campaignCollectionId: dataFilePreview.context.journeyId,
  });

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? null
    : platforms.find(({ name }) => name === value);

  const sortedPlatforms = platforms.toSorted(
    (a, b) =>
      groupPlatformByType[a.type].order - groupPlatformByType[b.type].order,
  );

  return (
    <Combobox
      data-dd-action-name="Validated cell: select platform"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select platform')}
      inputValue={fieldValue?.label}
      options={sortedPlatforms}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.name}
      groupBy={(option) => groupPlatformByType[option.type].label}
      onChange={(option) => {
        onChange(option.name, option.label);
      }}
      inputProps={{ autoComplete: 'off' }}
    />
  );
};

const PlatformFieldV2 = ({
  value,
  onChange,
  cell,
}: Pick<FieldProps<string>, 'value' | 'onChange' | 'cell'>) => {
  const { t } = useTranslation('@myn/import-hub');
  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V2'>(dataFileId);
  const {
    data: platforms = [],
    isLoading,
    noOptionsText,
  } = usePlatformsOfActivity(
    dataFilePreview.context.activityId as string,
    dataFilePreview.granularityLevel,
  );

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? null
    : platforms.find(({ id }) => id === value);

  return (
    <Combobox
      data-dd-action-name="Validated cell: select platform"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select platform')}
      inputValue={fieldValue?.name}
      options={platforms}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id}
      noOptionsText={noOptionsText}
      onChange={(option) => {
        onChange(option.id, option.name);
      }}
      inputProps={{ autoComplete: 'off' }}
    />
  );
};

const groupPlatformByType = {
  two_way: {
    label: 'TWO-WAY',
    order: 1,
  },
  one_way: {
    label: 'ONE-WAY',
    order: 2,
  },
  virtual: {
    label: 'VIRTUAL',
    order: 3,
  },
} as const;

const groupChannelByType = {
  online: {
    label: 'ONLINE',
    order: 1,
  },
  offline: {
    label: 'OFFLINE',
    order: 2,
  },
} as const;

type ChannelTypeGroup = keyof typeof ChannelTypeCategory;

const ChannelField = ({
  value,
  onChange,
  cell,
  platformCell,
  mediaRowCell,
}: Pick<
  FieldProps<string>,
  'value' | 'onChange' | 'cell' | 'platformCell' | 'mediaRowCell'
>) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V1'>(dataFileId);

  const { data: channelsNames = [], isLoading } = useChannelNamesList({
    campaignCollectionId: dataFilePreview.context.journeyId,
    platformName: platformCell.error ? null : platformCell.rawCell,
    tileId: mediaRowCell?.error ? null : mediaRowCell?.rawCell,
  });

  const {
    data: { channelsByName },
  } = useChannelsCatalog();
  const channelsData = channelsNames.map((name) => ({
    ...channelsByName[name],
    label: channelsByName[name].label,
    name,
  }));

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? undefined
    : channelsData.find(({ name }) => name === value);

  return (
    <Combobox
      data-dd-action-name="Validated cell: select channel"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select channel *')}
      inputValue={fieldValue?.label}
      onChange={(option) => {
        onChange(option.name, option.label);
      }}
      groupBy={(option) =>
        groupChannelByType[option.type as ChannelTypeGroup].label
      }
      options={channelsData.toSorted(
        (a, b) =>
          groupChannelByType[a.type as ChannelTypeGroup].order -
          groupChannelByType[b.type as ChannelTypeGroup].order,
      )}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.name}
      inputProps={{ autoComplete: 'off' }}
    />
  );
};

const ChannelFieldV2 = ({
  value,
  onChange,
  cell,
  platformCell,
  mediaRowCell,
}: Pick<
  FieldProps<string>,
  'value' | 'onChange' | 'cell' | 'platformCell' | 'mediaRowCell'
>) => {
  const { t } = useTranslation('@myn/import-hub');

  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V2'>(dataFileId);

  const { data: channelsNames = [], isLoading } = useActivityChannelNamesList(
    dataFilePreview.context.activityId as string,
    {
      platformName: platformCell.error ? null : platformCell.rawCell,
      mediaRowItemId: mediaRowCell?.error ? null : mediaRowCell?.rawCell,
    },
  );

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const channelsData = channelsNames.map((name) => ({
    label: channelsByName[name].label,
    name,
  }));

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? undefined
    : channelsData.find(({ name }) => name === value);

  return (
    <Combobox
      data-dd-action-name="Validated cell: select channel"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select channel *')}
      inputValue={fieldValue?.label}
      onChange={(option) => {
        onChange(option.name, option.label);
      }}
      options={channelsData}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.name}
      inputProps={{ autoComplete: 'off' }}
    />
  );
};

const MediaRowField = ({
  value,
  onChange,
  cell,
  platformCell,
  channelCell,
}: Pick<
  FieldProps<string>,
  'value' | 'onChange' | 'cell' | 'platformCell' | 'channelCell'
>) => {
  const { t } = useTranslation('@myn/import-hub');
  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V1'>(dataFileId);

  const { data: mediaRowsList = [], isLoading } = useMediaRowslist({
    campaignCollectionId: dataFilePreview.context.journeyId as number,
    platformName: platformCell?.error ? null : platformCell?.rawCell,
    channelName: channelCell?.error ? null : channelCell?.rawCell,
  });

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? null
    : mediaRowsList.find(({ name }) => name === value);

  return (
    <Combobox
      data-dd-action-name="Validated cell: select media row"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select media row *')}
      inputValue={fieldValue?.name}
      options={mediaRowsList}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => String(option.tileId)}
      onChange={(option) => {
        onChange(String(option.tileId), option.name);
      }}
      inputProps={{ autoComplete: 'off' }}
      menuItemClassName="grid-cols-[1fr]"
      renderOption={(option) => {
        const channelCatalogItem = channelsByName[option.channelName];

        return (
          <div
            className="flex w-full items-center justify-between gap-2 overflow-hidden"
            data-dd-action-name="Media row option"
          >
            <div className="min-w-0 flex-1">
              <TruncateText>
                <div>{option.name}</div>
              </TruncateText>
            </div>
            {option.channelName ? (
              <Chip
                variant="outline"
                size="medium"
                color="secondary"
                className="capitalize"
              >
                {channelCatalogItem.label}
                {channelCatalogItem.logo ? (
                  <Icon component={channelCatalogItem.logo} />
                ) : (
                  <Settings className="fill-icon-primary" />
                )}
              </Chip>
            ) : null}
          </div>
        );
      }}
    />
  );
};

const MediaRowFieldV2 = ({
  value,
  onChange,
  cell,
  platformCell,
  channelCell,
}: Pick<
  FieldProps<string>,
  'value' | 'onChange' | 'cell' | 'platformCell' | 'channelCell'
>) => {
  const { t } = useTranslation('@myn/import-hub');
  const { dataFileId } = useDataFileContext();
  const { data: dataFilePreview } = useDataFilePreview<'V2'>(dataFileId);

  const { data: mediaRowsList = [], isLoading } = useActivityMediaRowsList(
    dataFilePreview.context.activityId as string,
    {
      platformName: platformCell?.error ? null : platformCell?.rawCell,
      channelName: channelCell?.error ? null : channelCell?.rawCell,
    },
  );

  const {
    data: { channelsByName },
  } = useChannelsCatalog();

  const isInvalidValue = cell.error === 'type_invalid';

  const fieldValue = isInvalidValue
    ? null
    : mediaRowsList.find(({ name }) => name === value);

  return (
    <Combobox
      data-dd-action-name="Validated cell: select media row"
      open={true}
      isLoading={isLoading}
      placeholder={t('Select media row *')}
      inputValue={fieldValue?.name}
      options={mediaRowsList}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.mediaItemId}
      onChange={(option) => {
        onChange(option.mediaItemId, option.name);
      }}
      inputProps={{ autoComplete: 'off' }}
      menuItemClassName="grid-cols-[1fr]"
      renderOption={(option) => {
        const channelCatalogItem = channelsByName[option.channelName];

        return (
          <div
            className="flex w-full items-center justify-between gap-2 overflow-hidden"
            data-dd-action-name="Media row option"
          >
            <div className="min-w-0 flex-1">
              <TruncateText>
                <div>{option.name}</div>
              </TruncateText>
            </div>
            {option.channelName ? (
              <Chip
                variant="outline"
                size="medium"
                color="secondary"
                className="capitalize"
              >
                {channelCatalogItem.label}
                {channelCatalogItem.logo ? (
                  <Icon component={channelCatalogItem.logo} />
                ) : (
                  <Settings className="fill-icon-primary" />
                )}
              </Chip>
            ) : null}
          </div>
        );
      }}
    />
  );
};

type FieldVariant<T> = {
  component: FC<FieldProps<T>>;
  transforms: {
    toClient: (v: string) => T;
    toServer: (v: T) => string;
  };
};

const useFieldVariant = (formatType: CellFormatType) => {
  const isImportHubV2 = useImportHubV2();

  const fieldVariants = {
    id: {
      component: IdTextField,
      transforms: {
        toClient: (v) => v,
        toServer: (v) => v,
      },
    } as FieldVariant<ComponentProps<typeof IdTextField>['value']>,
    date: {
      component: DateField,
      transforms: {
        toClient: (v) => {
          const date = parseISO(v);
          return isValid(date) ? date : null;
        },
        toServer: (v) => {
          return isValid(v)
            ? formatISO(v as Date, { representation: 'date' })
            : '';
        },
      },
    } as FieldVariant<ComponentProps<typeof DateField>['value']>,
    int: {
      component: CounterField,
      transforms: {
        toClient: Number,
        toServer: String,
      },
    } as FieldVariant<ComponentProps<typeof CounterField>['value']>,
    float: {
      component: FloatField,
      transforms: {
        toClient: (v) => (Number.isNaN(Number(v)) ? null : v),
        toServer: (v) => (v === null ? '0' : v),
      },
    } as FieldVariant<ComponentProps<typeof FloatField>['value']>,
    platform: {
      component: isImportHubV2 ? PlatformFieldV2 : PlatformField,
      transforms: {
        toClient: (v) => v,
        toServer: (v) => v,
      },
    } as FieldVariant<ComponentProps<typeof PlatformField>['value']>,
    channel: {
      component: isImportHubV2 ? ChannelFieldV2 : ChannelField,
      transforms: {
        toClient: (v) => v,
        toServer: (v) => v,
      },
    } as FieldVariant<ComponentProps<typeof ChannelField>['value']>,
    media_row: {
      component: isImportHubV2 ? MediaRowFieldV2 : MediaRowField,
      transforms: {
        toClient: (v) => v,
        toServer: (v) => v,
      },
    } as FieldVariant<ComponentProps<typeof MediaRowField>['value']>,
  };

  return fieldVariants[formatType];
};
